import { useTranslation } from 'react-i18next';
import { useResetRolePermissionsMutation, useRolePermissionsQuery, useSetRolePermissionMutation } from '@platform/state/api';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { Spinner } from '@platform/ui';
import { SysTable } from '../system/SysTable';
import { SystemPage } from '../system/SystemPage';
import { useFailToast } from '../system/useFailToast';

/** Ролі прототипу v4 (ROLES): інші ролі платформи (партнер) показуються, лише коли в них є учасники. */
const PROTOTYPE_ROLES = ['Owner', 'Admin', 'Member', 'Guest'];

/**
 * «Ролі й дозволи» (прототип v4 G:L7881–7930, pgAdmRoles): матриця «Можливість × Власник · Адміністратор · Учасник · Гість» з кількістю
 * людей у кожній ролі (GET /role-permissions; інші ролі платформи — лише з учасниками). Клітинка змінюється одразу (PUT …/{роль}/{можливість}); власник — завжди все (стовпець
 * заблоковано); змінювати може лише власник простору (canEdit). Змінену матрицю повертає до типової «Скинути до типових» (DELETE).
 */
export function AdminRolesPage() {
  const { t } = useTranslation();
  const { ws } = useWorkspaceParams();
  const { data } = useRolePermissionsQuery(ws, { skip: !ws });
  const [setCell] = useSetRolePermissionMutation();
  const [reset, resetState] = useResetRolePermissionsMutation();
  const fail = useFailToast();
  if (!data) return <Spinner />;
  const roles = data.roles.filter((r) => PROTOTYPE_ROLES.includes(r.role) || r.members > 0);
  const columns = `minmax(220px, 1.6fr) repeat(${roles.length}, minmax(90px, 1fr))`;
  const head = [t('sysAdmin.roles.capability'), ...roles.map((r) => (
    <span key={r.role} className="ad-rolehead">
      <strong>{t(`roles.${r.role}`, { defaultValue: r.role })}</strong>
      <span>{t('sysAdmin.roles.people', { count: r.members })}</span>
    </span>
  ))];
  const rows = data.capabilities.map((cap) => ({
    key: cap,
    cells: [<span key="l" className="ad-cap">{t(`sysAdmin.caps.${cap}`, { defaultValue: cap })}</span>, ...roles.map((r) => {
      const on = r.locked || r.allowed.includes(cap);
      const why = r.locked ? t('sysAdmin.roles.ownerAll') : !data.canEdit ? t('sysAdmin.roles.onlyOwner') : undefined;
      return (
        <span key={r.role} className="ad-check">
          <input type="checkbox" checked={on} disabled={r.locked || !data.canEdit} title={why} aria-label={`${t(`sysAdmin.caps.${cap}`, { defaultValue: cap })} · ${t(`roles.${r.role}`, { defaultValue: r.role })}`}
            onChange={() => setCell({ ws, role: r.role, capability: cap, allowed: !on }).unwrap().catch(fail)} />
        </span>
      );
    })],
  }));
  const resetButton = data.changed && data.canEdit
    ? <button type="button" className="sp-btn sp-btn--danger ad-reset" disabled={resetState.isLoading} onClick={() => reset(ws).unwrap().catch(fail)}>{t('sysAdmin.roles.reset')}</button>
    : undefined;
  return (
    <SystemPage title={t('sysPages.sections.roles')} sub={t('sysAdmin.roles.sub')} actions={resetButton}>
      <div className="ad-roles"><SysTable columns={columns} minWidth={640} head={head} rows={rows} label={t('sysPages.sections.roles')} /></div>
    </SystemPage>
  );
}
